import {
  countCached,
  logFailure,
  newestRkey,
  putPullMeta,
  upsertRecords,
  type CachedRecordRow,
} from '../cache/db'
import { activeAccount } from '../store/accounts'
import { api, withFreshAccess } from './client'

/**
 * Bluesky collection identifiers we know how to walk. The values are the
 * lexicon NSIDs as they appear on a PDS.
 */
export const COLLECTIONS = {
  posts: 'app.bsky.feed.post',
  reposts: 'app.bsky.feed.repost',
  likes: 'app.bsky.feed.like',
  follows: 'app.bsky.graph.follow',
  blocks: 'app.bsky.graph.block',
  lists: 'app.bsky.graph.list',
  packs: 'app.bsky.graph.starterpack',
  feeds: 'app.bsky.feed.generator',
} as const

export type CollectionKey = keyof typeof COLLECTIONS

const PAGE_LIMIT = 100

interface ListRecordsResp {
  records?: Array<{
    uri: string
    cid?: string
    value: Record<string, unknown>
  }>
  cursor?: string
}

export interface PullProgress {
  handle: string
  collection: CollectionKey
  pulled: number
  /** Set when the walker stopped. */
  stoppedAt?: 'cursor-end' | 'reached-cache' | 'aborted' | 'error'
  error?: string
}

/**
 * Walk a single collection from any repo (default: the active account),
 * page by page, writing every record into IndexedDB under that target's
 * handle. Stops early when it hits an rkey we've already cached (default
 * behaviour) so a re-run is a fast incremental sync.
 *
 * Per the architecture invariant, all caching is browser-local — this
 * function never touches Cloudflare storage. The worker is just a
 * stateless XRPC proxy.
 *
 * The auth account (whose JWT signs the listRecords call) is always the
 * active account; the TARGET (whose records we actually pull) is opts
 * .targetHandle/targetDid when set, else the active account itself.
 */
export async function pullCollection(
  collection: CollectionKey,
  opts: {
    targetHandle?: string
    targetDid?: string
    /** Pull from scratch even if we already have entries. */
    full?: boolean
    /** Fired after each successful page write. */
    onProgress?: (p: PullProgress) => void
    /** Cancellation. */
    signal?: AbortSignal
  } = {},
): Promise<PullProgress> {
  const acc = activeAccount()
  if (!acc) {
    return {
      handle: opts.targetHandle ?? '',
      collection,
      pulled: 0,
      stoppedAt: 'error',
      error: 'no active account (need a session to call listRecords)',
    }
  }
  const handle = (opts.targetHandle ?? acc.handle).toLowerCase()
  // HARD GUARD: pulling someone else's repo requires their DID explicitly.
  // The old silent fallback to acc.did pulled MY records and filed them
  // under THEIR handle (v1.40.0 archiver bug) — never again.
  if (handle !== acc.handle.toLowerCase() && !opts.targetDid) {
    return {
      handle,
      collection,
      pulled: 0,
      stoppedAt: 'error',
      error: `targetDid required to pull @${handle} (refusing repo fallback to the active account)`,
    }
  }
  const repo = opts.targetDid ?? acc.did
  const nsid = COLLECTIONS[collection]
  const stopAtRkey = opts.full ? null : await newestRkey(handle, nsid)
  let cursor: string | undefined
  let pulled = 0

  const writeMeta = async () => {
    const tip = await newestRkey(handle, nsid)
    await putPullMeta({
      id: `${handle}|${collection}`,
      handle,
      collection,
      lastPulledAt: Date.now(),
      lastPulledRkey: tip,
      lastDelta: pulled,
      lastKnownTotal: null,
    })
  }

  while (true) {
    if (opts.signal?.aborted) {
      return { handle, collection, pulled, stoppedAt: 'aborted' }
    }
    const qs = new URLSearchParams({
      repo,
      collection: nsid,
      limit: String(PAGE_LIMIT),
    })
    if (cursor) qs.set('cursor', cursor)
    let resp: ListRecordsResp
    try {
      resp = await withFreshAccess(acc.id, () =>
        api.xrpc<ListRecordsResp>({
          accountId: acc.id,
          method: 'GET',
          path: `com.atproto.repo.listRecords?${qs.toString()}`,
        }),
      )
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err)
      // Log here, at the single place every pull error surfaces — the
      // callers (autoSync, Profile pull-all, per-records pulls, Home)
      // each render this error their own way and none of them logged it.
      const status = error.match(/\b([45]\d\d)\b/)
      void logFailure({
        op: 'sync',
        target: `sync:${collection} [repo=${repo}]`,
        accountDid: acc.did,
        reason: error,
        httpStatus: status ? Number(status[1]) : undefined,
      })
      return { handle, collection, pulled, stoppedAt: 'error', error }
    }
    const rows: CachedRecordRow[] = []
    let hitCachedTip = false
    for (const r of resp.records ?? []) {
      const rkey = rkeyFromUri(r.uri)
      if (!rkey) continue
      if (stopAtRkey && rkey <= stopAtRkey) {
        hitCachedTip = true
        continue
      }
      rows.push({
        handle,
        collection: nsid,
        rkey,
        cid: r.cid ?? null,
        uri: r.uri,
        value: r.value,
        fetched_at: Date.now(),
      })
    }
    if (rows.length > 0) {
      await upsertRecords(rows)
      pulled += rows.length
    }
    opts.onProgress?.({ handle, collection, pulled })
    if (hitCachedTip && !opts.full) {
      await writeMeta()
      notifyPulled(pulled)
      return { handle, collection, pulled, stoppedAt: 'reached-cache' }
    }
    cursor = resp.cursor
    if (!cursor) {
      await writeMeta()
      notifyPulled(pulled)
      return { handle, collection, pulled, stoppedAt: 'cursor-end' }
    }
  }
}

/** Nudge the disk archiver when a pull landed new rows — covers direct
 *  profile visits + "pull all", which never touch the gallery poll loop.
 *  The archiver listener debounces, so bursts of collections collapse. */
function notifyPulled(pulled: number): void {
  if (pulled <= 0) return
  try {
    window.dispatchEvent(new CustomEvent('bskyhq:gallery-refreshed'))
  } catch {
    /* non-window context — periodic fallback covers it */
  }
}

function rkeyFromUri(uri: string): string | null {
  const i = uri.lastIndexOf('/')
  return i < 0 ? null : uri.slice(i + 1)
}

/** Live count of cached records for the active account's handle. */
export async function activeCount(collection: CollectionKey): Promise<number> {
  const acc = activeAccount()
  if (!acc) return 0
  return await countCached(acc.handle.toLowerCase(), COLLECTIONS[collection])
}

/** Cache-count helper for an arbitrary target handle. */
export async function targetCount(
  targetHandle: string,
  collection: CollectionKey,
): Promise<number> {
  return await countCached(targetHandle.toLowerCase(), COLLECTIONS[collection])
}
